/**
 * Sorting helpers for the competitor report Posts table.
 *
 * Each accessor deliberately mirrors the expression the matching table cell
 * renders, so the sort order always reflects what the user can actually see.
 * That includes the quirks: the API returns different field names for YouTube
 * and Instagram, and the "Category" / "Genre" headers render the influencer
 * tier and the category name respectively (not the other way round).
 *
 * The three derivation helpers are injected rather than imported so this module
 * stays free of component dependencies and is testable on its own.
 */

// Columns offered as sortable, in the order they appear in the table. The
// thumbnail ("Post") and staff-only "Actions" columns are intentionally absent.
export const POST_SORT_COLUMNS = [
  { key: 'influencer', label: 'Influencer', numeric: false },
  { key: 'category', label: 'Category', numeric: false },
  { key: 'genre', label: 'Genre', numeric: false },
  { key: 'brand', label: 'Brand', numeric: false },
  { key: 'platform', label: 'Platform', numeric: false },
  { key: 'format', label: 'Format', numeric: false },
  { key: 'engagement', label: 'Engagement %', numeric: true },
  { key: 'views', label: 'Views', numeric: true },
  { key: 'likes', label: 'Likes', numeric: true },
  { key: 'comments', label: 'Comments', numeric: true },
  { key: 'date', label: 'Date', numeric: true },
];

const text = (value) => String(value || '').toLowerCase();

const likesOf = (post) => post.likes_count || post.like_count || 0;
const commentsOf = (post) => post.comments_count || post.comment_count || 0;
const sharesOf = (post) => post.shares_count || post.share_count || 0;
const viewsOf = (post) => post.view_count || post.reach || 0;

const creatorNameOf = (post) =>
  post.creator_details?.name ||
  (post.dump && post.dump.snippet && post.dump.snippet.channelTitle) ||
  post.creator_name ||
  post.creator ||
  post.handle ||
  post.channel_name ||
  '';

const postDateOf = (post) =>
  post.timestamp ||
  post.created_time ||
  post.published_at ||
  post.post_creation_date;

/**
 * Resolve the value a given column should be sorted by.
 *
 * @param {object} post    A post row as returned by the API.
 * @param {string} field   One of the POST_SORT_COLUMNS keys.
 * @param {object} helpers { getInfluencerType, cleanCategoryName, calculateEngagementRate }
 * @returns {string|number} Lowercased string for text columns, number otherwise.
 */
export const getPostSortValue = (post, field, helpers) => {
  const { getInfluencerType, cleanCategoryName, calculateEngagementRate } = helpers;

  switch (field) {
    case 'influencer':
      return text(creatorNameOf(post));

    // The "Category" column renders the follower-derived influencer tier.
    case 'category':
      return text(getInfluencerType(post.creator_details?.followers));

    // The "Genre" column renders the cleaned category name.
    case 'genre':
      return text(cleanCategoryName(post.creator_details?.category || post.category));

    case 'brand':
      return text(post.brand);

    case 'platform':
      return text(post.platform || (post.post_type ? 'Instagram' : 'YouTube'));

    case 'format':
      return text(post.post_type || post.media_type || 'Post');

    case 'engagement': {
      const explicit = post.engagement_rate;
      if (explicit !== undefined && explicit !== null && explicit !== 0) return explicit;
      return calculateEngagementRate(
        likesOf(post),
        commentsOf(post),
        sharesOf(post),
        post.creator_details?.followers || viewsOf(post) || 1
      );
    }

    case 'views':
      return viewsOf(post);

    case 'likes':
      return likesOf(post);

    case 'comments':
      return commentsOf(post);

    case 'date': {
      const raw = postDateOf(post);
      if (!raw) return 0;
      const ms = new Date(raw).getTime();
      return Number.isNaN(ms) ? 0 : ms;
    }

    default:
      return 0;
  }
};

/**
 * Return a new array of posts ordered by the given field. The input is never
 * mutated; when no field is active the original reference is returned so
 * callers can rely on referential equality to skip work.
 */
export const sortPosts = (posts, field, direction, helpers) => {
  if (!field) return posts;

  return [...posts].sort((a, b) => {
    const av = getPostSortValue(a, field, helpers);
    const bv = getPostSortValue(b, field, helpers);

    const cmp =
      typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv));

    return direction === 'asc' ? cmp : -cmp;
  });
};
